import { useEffect, useState, type ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import Layout from './components/Layout'
import Dashboard from './pages/Dashboard'
import Employees from './pages/Employees'
import EmployeeProfile from './pages/EmployeeProfile'
import EmployeeForm from './pages/EmployeeForm'
import Documents from './pages/Documents'
import DocumentUpload from './pages/DocumentUpload'
import OrgUnits from './pages/OrgUnits'
import OrgUnitForm from './pages/OrgUnitForm'
import Leave from './pages/Leave'
import LeaveRequest from './pages/LeaveRequest'
import Intranet from './pages/Intranet'
import AnnouncementForm from './pages/AnnouncementForm'
import Learning from './pages/Learning'
import CourseDetail from './pages/CourseDetail'
import CourseForm from './pages/CourseForm'
import QuizTake from './pages/QuizTake'
import QuizForm from './pages/QuizForm'
import CoursePlayer from './pages/CoursePlayer'
import ContentForm from './pages/ContentForm'
import SessionForm from './pages/SessionForm'
import AssignmentForm from './pages/AssignmentForm'
import SearchResults from './pages/SearchResults'
import Profile from './pages/Profile'
import Attendance from './pages/Attendance'
import Payroll from './pages/Payroll'
import PayslipForm from './pages/PayslipForm'
import PayrollRunNew from './pages/PayrollRunNew'
import Company from './pages/Company'
import CompanyEdit from './pages/CompanyEdit'
import Login from './pages/Login'
import { getValidAccessToken, handleCallback } from './auth'

function FullPageSpinner({ label }: { label: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
      <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-slate-200 border-t-brand-600" />
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  )
}

/** Route guard: waits for a valid (possibly refreshed) access token. */
function Protected({ children }: { children: ReactNode }) {
  const [state, setState] = useState<'loading' | 'ok'>('loading')
  const location = useLocation()

  useEffect(() => {
    let cancelled = false
    getValidAccessToken().then((token) => {
      if (!cancelled && token) setState('ok')
    })
    return () => {
      cancelled = true
    }
  }, [location.pathname])

  if (state === 'loading') {
    // Check synchronously first to avoid flashing the spinner on every nav
    if (localStorage.getItem('dwp_access_token')) {
      // token exists but may be mid-refresh; keep spinner brief
    }
    return <FullPageSpinner label="Checking your session…" />
  }
  return <>{children}</>
}

/** Handles the OIDC redirect from Keycloak. */
function Callback() {
  const navigate = useNavigate()
  const [error, setError] = useState(false)

  useEffect(() => {
    handleCallback().then((ok) => {
      if (ok) {
        navigate('/', { replace: true })
      } else {
        setError(true)
      }
    })
  }, [navigate])

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50">
        <p className="text-sm font-semibold text-red-600">Sign-in failed. Please try again.</p>
        <button
          onClick={() => navigate('/login', { replace: true })}
          className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white"
        >
          Back to login
        </button>
      </div>
    )
  }
  return <FullPageSpinner label="Completing sign-in…" />
}

function App() {
  const location = useLocation()
  const hasToken = !!localStorage.getItem('dwp_access_token')
  const onPublicRoute = ['/login', '/callback'].includes(location.pathname)

  if (!hasToken && !onPublicRoute) {
    return <Navigate to="/login" replace />
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/callback" element={<Callback />} />
      <Route
        path="/*"
        element={
          <Protected>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/employees/new" element={<EmployeeForm />} />
                <Route path="/employees/:id" element={<EmployeeProfile />} />
                <Route path="/employees/:id/edit" element={<EmployeeForm />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/documents/upload" element={<DocumentUpload />} />
                <Route path="/org-units" element={<OrgUnits />} />
                <Route path="/org-units/new" element={<OrgUnitForm />} />
                <Route path="/org-units/:id/edit" element={<OrgUnitForm />} />
                <Route path="/leave" element={<Leave />} />
                <Route path="/leave/request" element={<LeaveRequest />} />
                <Route path="/intranet" element={<Intranet />} />
                <Route path="/intranet/new" element={<AnnouncementForm />} />
                <Route path="/intranet/:id/edit" element={<AnnouncementForm />} />
                <Route path="/learning" element={<Learning />} />
                <Route path="/courses/new" element={<CourseForm />} />
                <Route path="/courses/:id" element={<CourseDetail />} />
                <Route path="/courses/:id/edit" element={<CourseForm />} />
                <Route path="/courses/:id/quiz" element={<QuizTake />} />
                <Route path="/courses/:id/quiz/edit" element={<QuizForm />} />
                <Route path="/courses/:id/learn" element={<CoursePlayer />} />
                <Route path="/courses/:id/content/edit" element={<ContentForm />} />
                <Route path="/courses/:id/sessions/new" element={<SessionForm />} />
                <Route path="/courses/:id/assign" element={<AssignmentForm />} />
                <Route path="/sessions/:sessionId/edit" element={<SessionForm />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/payroll/payslips/new" element={<PayslipForm />} />
                <Route path="/payroll/payslips/:id/edit" element={<PayslipForm />} />
                <Route path="/payroll/runs/new" element={<PayrollRunNew />} />
                <Route path="/company" element={<Company />} />
                <Route path="/company/edit" element={<CompanyEdit />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </Protected>
        }
      />
    </Routes>
  )
}

export default App
